import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormField, Input, Select } from '@/shared/components';
import type { AddressSuggestionResponse } from '@/shared/api';
import { PropertyAddressAutocomplete } from '@/features/properties/property-form/PropertyAddressAutocomplete';
import type { AddressValues } from './objectEditForms';

const touched = { shouldDirty: true, shouldValidate: true } as const;
const COUNTRIES: [string, string][] = [
  ['DE', 'Deutschland'],
  ['AT', 'Österreich'],
  ['CH', 'Schweiz'],
];

/**
 * Name und Adresse des Objekts. Ein Treffer der Adresssuche füllt alle Felder samt Position;
 * wer ein Feld von Hand ändert, speichert die Adresse bewusst als ungeprüft und ohne Pin, damit
 * der Nachlauf die Position neu ermittelt.
 */
export function ObjectAddressFields({ form }: { form: UseFormReturn<AddressValues> }) {
  const { register, control, watch, setValue, formState } = form;
  const { errors } = formState;
  const countryCode = watch('countryCode');

  const markForReview = () => {
    setValue('latitude', null, { shouldDirty: true });
    setValue('longitude', null, { shouldDirty: true });
    setValue('needsAddressReview', true, { shouldDirty: true });
  };
  const takeSuggestion = ({ label, address, needsAddressReview }: AddressSuggestionResponse) => {
    setValue('addressQuery', label, { shouldDirty: true });
    setValue('streetName', address.streetName, touched);
    setValue('houseNumber', address.houseNumber, touched);
    setValue('postalCode', address.postalCode, touched);
    setValue('city', address.city, touched);
    setValue('countryCode', address.countryCode, touched);
    setValue('latitude', address.latitude, { shouldDirty: true });
    setValue('longitude', address.longitude, { shouldDirty: true });
    setValue('needsAddressReview', needsAddressReview, { shouldDirty: true });
  };
  const manual = { onChange: markForReview };
  // Ein Land außerhalb der Liste — etwa aus der Adresssuche — bleibt wählbar statt leer.
  const countries = COUNTRIES.some(([code]) => code === countryCode)
    ? COUNTRIES
    : [...COUNTRIES, [countryCode, countryCode] as [string, string]];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] gap-4">
      <FormField label="Objektname" error={errors.name?.message} required className="col-span-2">
        <Input {...register('name')} />
      </FormField>
      <FormField
        label="Adresse suchen"
        hint="Ein Treffer füllt die Felder darunter samt Position."
        className="col-span-2"
      >
        <Controller
          control={control}
          name="addressQuery"
          render={({ field }) => (
            <PropertyAddressAutocomplete
              value={field.value ?? ''}
              onChange={field.onChange}
              onSelect={takeSuggestion}
            />
          )}
        />
      </FormField>
      <FormField label="Straße" error={errors.streetName?.message} required>
        <Input {...register('streetName', manual)} />
      </FormField>
      <FormField label="Hausnummer" error={errors.houseNumber?.message} required>
        <Input {...register('houseNumber', manual)} />
      </FormField>
      <div className="col-span-2 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-4">
        <FormField label="PLZ" error={errors.postalCode?.message} required>
          <Input {...register('postalCode', manual)} />
        </FormField>
        <FormField label="Ort" error={errors.city?.message} required>
          <Input {...register('city', manual)} />
        </FormField>
      </div>
      <FormField label="Land" error={errors.countryCode?.message} required className="col-span-2">
        <Select {...register('countryCode', manual)}>
          {countries.map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </Select>
      </FormField>
    </div>
  );
}
